/**
 * Edit Template Dialog
 * Edit metadata (name, category, description) and optionally replace the
 * underlying .docx file. Replacing the file re-extracts variables on the
 * server.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Loader2, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/api/client';
import { cn } from '@/lib/utils';

const CATEGORIES = [
  { value: 'engagement_letter', label: 'Engagement Letter' },
  { value: 'tax_return_confirmation', label: 'Tax Return Confirmation' },
  { value: 'income_estimate', label: 'Income Estimate' },
  { value: 'minutes', label: 'Minutes' },
  { value: 'solvency_resolution', label: 'Solvency Resolution' },
  { value: 'meeting_minutes', label: 'Meeting Minutes' },
  { value: 'compliance_letter', label: 'Compliance Letter' },
  { value: 'fee_notice', label: 'Fee Notice' },
  { value: 'general', label: 'General' },
];

const MAX_SIZE_BYTES = 5 * 1024 * 1024;

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export interface EditTemplateDialogTemplate {
  id: string;
  name?: string;
  category?: string;
  description?: string;
  originalFilename?: string;
  original_filename?: string;
  variableCount?: number;
  variable_count?: number;
}

interface EditTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: EditTemplateDialogTemplate | null;
}

export function EditTemplateDialog({ open, onOpenChange, template }: EditTemplateDialogProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('general');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  // Reset local state whenever the dialog is (re)opened with a template
  useEffect(() => {
    if (template && open) {
      setName(template.name || '');
      setCategory(template.category || 'general');
      setDescription(template.description || '');
      setFile(null);
      setFileError(null);
    }
  }, [template, open]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!template) throw new Error('No template');
      const payload: any = {
        name,
        category,
        description: description || null,
      };
      if (file) {
        payload.fileBase64 = await fileToBase64(file);
        payload.originalFilename = file.name;
      }
      const resp = await apiClient.put(`/automation/templates/${template.id}`, payload);
      return resp.data?.data || resp.data;
    },
    onSuccess: (data: any) => {
      if (file) {
        const varCount = data?.variable_count ?? data?.variableCount ?? 0;
        toast.success(`Template updated. ${varCount} variables detected from the new file.`);
      } else {
        toast.success('Template updated.');
      }
      queryClient.invalidateQueries({ queryKey: ['automation', 'templates'] });
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to update template');
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError(null);
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    if (!f.name.toLowerCase().endsWith('.docx')) {
      setFileError('Only .docx files are supported');
      setFile(null);
      return;
    }
    if (f.size > MAX_SIZE_BYTES) {
      setFileError('File too large (max 5MB)');
      setFile(null);
      return;
    }
    setFile(f);
  };

  const currentFilename = template?.originalFilename || template?.original_filename;
  const canSubmit = !!template && !!name.trim() && !updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Template</DialogTitle>
          <DialogDescription>
            Update the template's name, category, or description. Optionally replace the Word file — variables will be re-extracted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-tmpl-name">Name *</Label>
            <Input id="edit-tmpl-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-tmpl-category">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="edit-tmpl-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-tmpl-desc">Description</Label>
            <Textarea id="edit-tmpl-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          </div>

          <div className="space-y-2">
            <Label>Word File (.docx)</Label>
            {file ? (
              <div className="flex items-center gap-2 rounded-md border p-2 bg-muted/30">
                <FileText className="h-4 w-4 text-blue-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{file.name}</p>
                  <p className="text-[11px] text-muted-foreground">{(file.size / 1024).toFixed(1)} KB · will replace existing file</p>
                </div>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setFile(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                {currentFilename && (
                  <div className="flex items-center gap-2 rounded-md border p-2 bg-muted/20">
                    <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                    <p className="text-sm text-muted-foreground truncate flex-1">Current: {currentFilename}</p>
                  </div>
                )}
                <label className={cn(
                  'flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-md py-4 cursor-pointer hover:bg-muted/40 transition-colors',
                  fileError && 'border-destructive'
                )}>
                  <Upload className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Click to replace the .docx (optional)</span>
                  <span className="text-[11px] text-muted-foreground">Max 5MB — variables will be re-extracted</span>
                  <input type="file" accept=".docx" className="hidden" onChange={handleFileChange} />
                </label>
              </>
            )}
            {fileError && <p className="text-xs text-destructive">{fileError}</p>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={updateMutation.isPending}>Cancel</Button>
          <Button onClick={() => updateMutation.mutate()} disabled={!canSubmit}>
            {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditTemplateDialog;
